// Find the child (plan §6 step 2): first letters on an on-screen keyboard (the OS keyboard would
// cover half the tablet), a room filter, today's list, and "Scan a code" for a guardian pass.
import { useMemo, useState } from "react";
import { Button, EmptyState, Icon, PhotoTile } from "@vavr/ui";
import { searchChildren, type Roster } from "@vavr/client";
import { usePhoto, type PhotoSource } from "../photos.ts";
import type { Child, Direction } from "./machine.ts";

export const ROWS = ["ABCDEFG", "HIJKLMN", "OPQRSTU", "VWXYZ"];
const MAX_RESULTS = 24;

function ChildTile({ child, photos, room, onSelect }: { child: Child; photos: PhotoSource; room: string | undefined; onSelect: () => void }) {
  const src = usePhoto(photos, child.photo_sha256);
  return <PhotoTile kind="child" name={`${child.first} ${child.last}`} detail={room} src={src} onSelect={onSelect} />;
}

export interface FindChildProps {
  roster: Roster;
  direction: Direction;
  photos: PhotoSource;
  /** Children handled on this device today, most recent first ("here today" for pickups). */
  today: string[];
  onSelect: (childId: string) => void;
  onScan?: () => void;
}

export function FindChild({ roster, direction, photos, today, onSelect, onScan }: FindChildProps) {
  const [q, setQ] = useState("");
  const [room, setRoom] = useState<string | null>(null);
  const rooms = useMemo(() => new Map(roster.rooms.map((r) => [r.id, r.name])), [roster]);
  const active = useMemo(() => roster.children.filter((c) => c.status !== "withdrawn"), [roster]);

  const results = useMemo(() => {
    const scoped = { ...roster, children: active };
    if (q || room) return searchChildren(scoped, q, room).sort((a, b) => a.first.localeCompare(b.first));
    const rank = new Map(today.map((id, i) => [id, i]));
    return [...active].sort((a, b) => (rank.get(a.id) ?? 1e9) - (rank.get(b.id) ?? 1e9) || a.first.localeCompare(b.first));
  }, [roster, active, q, room, today]);

  const heading = q || room ? `${results.length} ${results.length === 1 ? "child" : "children"}` : today.length ? "Here today" : "All children";

  return (
    <div className="pmk-find">
      <section className="pmk-find__keys" aria-label="Type the first letters of the name">
        <output className="pmk-find__query" aria-live="polite" aria-label="Letters typed">{q || <span className="pmk-find__placeholder">First letters</span>}</output>
        <div className="pmk-letters" role="group" aria-label="Letters">
          {ROWS.map((row) => (
            <div key={row} className="pmk-letters__row">
              {[...row].map((ch) => (
                <button key={ch} type="button" className="pmk-letters__key" onClick={() => setQ((x) => (x + ch).slice(0, 20))}>{ch}</button>
              ))}
              {row === "VWXYZ" && (
                <>
                  <button type="button" className="pmk-letters__key pmk-letters__key--wide" onClick={() => setQ((x) => (x.endsWith(" ") || !x ? x : `${x} `))} aria-label="Space">Space</button>
                  <button type="button" className="pmk-letters__key" onClick={() => setQ((x) => x.slice(0, -1))} aria-label="Delete last letter"><Icon name="backspace" /></button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="pmk-find__actions">
          <Button variant="secondary" onClick={() => { setQ(""); setRoom(null); }} disabled={!q && !room}>Clear</Button>
          {onScan && <Button variant="secondary" icon="qr" onClick={onScan}>Scan a code</Button>}
        </div>
      </section>
      <section className="pmk-find__results" aria-label={direction === "pickup" ? "Children to collect" : "Children to drop off"}>
        {roster.rooms.length > 1 && (
          <div className="pmk-chips" role="group" aria-label="Room">
            <button type="button" className="pmk-chip" aria-pressed={room === null} onClick={() => setRoom(null)}>All rooms</button>
            {roster.rooms.map((r) => (
              <button key={r.id} type="button" className="pmk-chip" aria-pressed={room === r.id} onClick={() => setRoom(room === r.id ? null : r.id)}>{r.name}</button>
            ))}
          </div>
        )}
        <h2 className="pmk-find__heading">{heading}</h2>
        {results.length ? (
          <div className="pmk-tiles">
            {results.slice(0, MAX_RESULTS).map((c) => (
              <ChildTile key={c.id} child={c} photos={photos} room={c.room_id ? rooms.get(c.room_id) : undefined} onSelect={() => onSelect(c.id)} />
            ))}
          </div>
        ) : (
          <EmptyState icon="search" title="No child with those letters" description="Check the spelling, or ask a member of staff." />
        )}
      </section>
    </div>
  );
}
